/**
 * @file DeleteLegalDialog.tsx
 * @description Окно «Удалить» (phase19.16, SB16, SB143; uiux-legal-steps-b-c
 * §9.2). Три случая:
 *   - документ с редакциями удалить нельзя — окно-объяснение и переход
 *     к «Снять с публикации…» (тот же текст при 409 `legal_delete_has_revisions`);
 *   - черновик без редакций, своя группа, автоматическая группа — вопрос
 *     и «что изменится» по `usage` (пункты меню и строки подвала останутся
 *     без цели — их нужно убрать).
 * На телефоне — во весь экран.
 */

import { useState } from "react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { NoticeStrip } from "../ui/NoticeStrip";
import { removeLegalDoc, type LegalDocumentDto } from "../../lib/api-legal";
import { legalErrorCode, legalErrorText } from "../../lib/legal/legal-errors";
import { canDeleteLegalDoc, deleteBlockedText, deleteConsequences, deleteIntro } from "../../lib/legal/legal-usage-lines";

interface Props {
  /** Документ; null — окно закрыто. */
  doc: LegalDocumentDto | null;
  onClose: () => void;
  onDeleted: () => void;
  /** Перейти к снятию с публикации (из окна «нельзя удалить»). */
  onUnpublish: () => void;
}

export function DeleteLegalDialog({ doc, onClose, onDeleted, onUnpublish }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Сервер сказал «есть редакции», хотя по карточке их не было (карточка устарела).
  const [serverBlocked, setServerBlocked] = useState(false);
  const blocked = doc !== null && (serverBlocked || !canDeleteLegalDoc(doc));

  const close = () => {
    if (busy) return;
    setError(null);
    setServerBlocked(false);
    onClose();
  };

  async function submit() {
    if (!doc || busy) return;
    setBusy(true);
    setError(null);
    try {
      await removeLegalDoc(doc.id);
      onDeleted();
    } catch (err) {
      if (legalErrorCode(err) === "legal_delete_has_revisions") setServerBlocked(true);
      else setError(legalErrorText(err, "Не удалось удалить документ"));
    } finally {
      setBusy(false);
    }
  }

  const intro = doc ? deleteIntro(doc) : null;
  const lines = doc && !blocked ? deleteConsequences(doc) : [];

  return (
    <AlertDialog open={doc !== null} onOpenChange={(open) => (!open ? close() : null)}>
      <AlertDialogContent fullScreen className="max-w-md gap-3 sm:max-w-[30rem]">
        <AlertDialogHeader>
          <AlertDialogTitle>{blocked ? "Этот документ нельзя удалить" : intro?.title}</AlertDialogTitle>
          <AlertDialogDescription>{blocked && doc ? deleteBlockedText(doc) : intro?.text}</AlertDialogDescription>
        </AlertDialogHeader>
        {lines.length > 0 ? (
          <ul className="type-body flex list-disc flex-col gap-1 pl-5 text-on-surface">
            {lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        ) : null}
        {error ? <NoticeStrip tone="error">{error}</NoticeStrip> : null}
        <AlertDialogFooter>
          <Button variant="ghost" className="max-sm:flex-1" onClick={close} disabled={busy}>
            {blocked ? "Закрыть" : "Отмена"}
          </Button>
          {blocked ? (
            doc?.isPublished ? (
              <Button
                className="max-sm:flex-1"
                onClick={() => {
                  setServerBlocked(false);
                  onUnpublish();
                }}
              >
                Снять с публикации…
              </Button>
            ) : null
          ) : (
            <Button variant="destructive-solid" className="max-sm:flex-1" loading={busy} onClick={() => void submit()}>
              {intro?.action}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
